import { SourceFolderEntry } from "./apiV2";

/**
 * The editor's unsaved work over an app's code, kept apart from the tree and
 * the files the API answers: those reload when the code changes, the work
 * has to survive that.
 *
 * `present` is every path that differs from the code as uploaded: a new file,
 * a file moved here from another path, a file whose text changed — or a mix
 * (moved and changed). `removed` is every uploaded path that is no longer
 * where it was: deleted, or moved to another path in `present`.
 *
 * Nothing here talks to the API. `editsBody` turns it into what
 * POST /source/edits takes; `overlayFolder` draws it over a folder listing.
 */

export interface PresentFile {
  /** The uploaded path this came from; null for a file made in the editor. */
  from: string | null;
  /** The text as edited; null when the bytes are still the uploaded file's. */
  content: string | null;
  /** The uploaded text, once read; null for a new file, or not read yet. */
  original: string | null;
}

export interface SourceWorkspace {
  present: Record<string, PresentFile>;
  removed: Record<string, "deleted" | { movedTo: string }>;
}

export const EMPTY_WORKSPACE: SourceWorkspace = { present: {}, removed: {} };

export type FileStatus = "new" | "renamed" | "modified" | "deleted" | null;

export const dirOf = (path: string) =>
  path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
export const nameOf = (path: string) => path.slice(path.lastIndexOf("/") + 1);

// The test runner's jsdom has no TextEncoder; a character count is close enough there.
const byteLength = (text: string) =>
  typeof TextEncoder === "undefined"
    ? text.length
    : new TextEncoder().encode(text).length;

const without = <T>(record: Record<string, T>, key: string) => {
  const { [key]: dropped, ...rest } = record;
  void dropped;
  return rest;
};

/** What the tree says beside a path: how it differs from the uploaded code. */
export const statusOf = (ws: SourceWorkspace, path: string): FileStatus => {
  const removed = ws.removed[path];
  if (removed === "deleted") return "deleted";
  const file = ws.present[path];
  if (!file) return null;
  if (file.from === null) return "new";
  if (file.from !== path) return "renamed";
  return file.content !== null ? "modified" : null;
};

/** Every path with unsaved work, for the count and the guard. */
export const dirtyPaths = (ws: SourceWorkspace): string[] => [
  ...Object.keys(ws.present).filter((path) => statusOf(ws, path) !== null),
  ...Object.keys(ws.removed).filter((path) => ws.removed[path] === "deleted"),
];

export const isDirty = (ws: SourceWorkspace) => dirtyPaths(ws).length > 0;

/** Where a path's bytes are read from: the uploaded path it came from. */
export const sourcePathOf = (
  ws: SourceWorkspace,
  path: string
): string | null => {
  const file = ws.present[path];
  return file ? file.from : ws.removed[path] ? null : path;
};

const valid = (path: string) =>
  path.length > 0 &&
  !path.startsWith("/") &&
  !path.endsWith("/") &&
  !path.includes("//") &&
  !path.includes("\\") &&
  !path
    .split("/")
    .some((segment) => segment === "." || segment === ".." || segment === "");

/** Whether a path may be written: well formed and not taken. `existing` says
 * which uploaded paths there are (the tree knows them; the API is the judge). */
export const pathProblem = (
  ws: SourceWorkspace,
  path: string,
  exists: (path: string) => boolean
): string | null => {
  if (!valid(path)) return "Use a relative path like src/app.js";
  const name = nameOf(path);
  if (name === ".env" || name.startsWith(".env.")) {
    return ".env files are never uploaded; set environment variables on the app instead";
  }
  if (path.split("/")[0] === ".git") return ".git is never uploaded";
  if (ws.present[path] || (exists(path) && ws.removed[path] === undefined)) {
    return "A file with this path already exists";
  }
  return null;
};

/**
 * The text of `path` as typed. `original` is the uploaded text it started
 * from (the file's, or the file it was moved from). Typing the original back
 * clears the change.
 */
export const setContent = (
  ws: SourceWorkspace,
  path: string,
  content: string,
  original: string | null
): SourceWorkspace => {
  const current = ws.present[path] || { from: path, content: null, original };
  const from = current.from;
  const unchanged = from !== null && content === (current.original ?? original);
  if (unchanged && from === path) {
    return { ...ws, present: without(ws.present, path) };
  }
  return {
    ...ws,
    present: {
      ...ws.present,
      [path]: {
        from,
        content: unchanged ? null : content,
        original: current.original ?? original,
      },
    },
  };
};

/** The uploaded text of `path`, once read, so a later edit can compare. */
export const withOriginal = (
  ws: SourceWorkspace,
  path: string,
  original: string
): SourceWorkspace => {
  const file = ws.present[path];
  if (!file || file.original !== null) return ws;
  return { ...ws, present: { ...ws.present, [path]: { ...file, original } } };
};

/** A file made in the editor, empty or with a first text. */
export const createFile = (
  ws: SourceWorkspace,
  path: string,
  content = ""
): SourceWorkspace => {
  const wasRemoved = ws.removed[path];
  return {
    // Creating at a path that was deleted takes its place: no longer removed.
    removed: wasRemoved === "deleted" ? without(ws.removed, path) : ws.removed,
    present: {
      ...ws.present,
      [path]:
        wasRemoved === "deleted"
          ? { from: path, content, original: null }
          : { from: null, content, original: null },
    },
  };
};

/** `path` (as shown now) is gone. An uploaded file is marked; a new one just vanishes. */
export const deleteFile = (
  ws: SourceWorkspace,
  path: string
): SourceWorkspace => {
  const file = ws.present[path];
  if (!file) {
    return { ...ws, removed: { ...ws.removed, [path]: "deleted" } };
  }
  const present = without(ws.present, path);
  if (file.from === null) {
    return { ...ws, present };
  }
  return { present, removed: { ...ws.removed, [file.from]: "deleted" } };
};

/** `path` (as shown now) is now at `to`. */
export const renameFile = (
  ws: SourceWorkspace,
  path: string,
  to: string
): SourceWorkspace => {
  if (path === to) return ws;
  const file = ws.present[path] || {
    from: path,
    content: null,
    original: null,
  };
  const present = { ...without(ws.present, path) };
  let removed = ws.removed;
  if (file.from !== null) {
    // Back where it was uploaded: no longer moved.
    removed =
      to === file.from
        ? without(removed, file.from)
        : { ...removed, [file.from]: { movedTo: to } };
  }
  present[to] = { ...file };
  if (to === file.from && file.content === null) {
    delete present[to];
  }
  return { present, removed };
};

/** Drops the unsaved work on one path (as shown now, or as deleted). */
export const discardFile = (
  ws: SourceWorkspace,
  path: string
): SourceWorkspace => {
  if (ws.removed[path] === "deleted") {
    return { ...ws, removed: without(ws.removed, path) };
  }
  const file = ws.present[path];
  if (!file) return ws;
  const present = without(ws.present, path);
  const removed =
    file.from !== null && file.from !== path
      ? without(ws.removed, file.from)
      : ws.removed;
  return { present, removed };
};

export interface EditsBody {
  baseUploadId: string;
  changed: { path: string; content: string }[];
  deleted: string[];
  renamed: { from: string; to: string }[];
}

/** What POST /source/edits takes. The API applies renames, then deletes, then changes. */
export const editsBody = (
  ws: SourceWorkspace,
  baseUploadId: string
): EditsBody => ({
  baseUploadId,
  renamed: Object.entries(ws.present)
    .filter(([path, file]) => file.from !== null && file.from !== path)
    .map(([path, file]) => ({ from: file.from as string, to: path })),
  deleted: Object.keys(ws.removed).filter(
    (path) => ws.removed[path] === "deleted"
  ),
  changed: Object.entries(ws.present)
    .filter(([, file]) => file.content !== null)
    .map(([path, file]) => ({ path, content: file.content as string })),
});

export type OverlaidEntry = SourceFolderEntry & {
  status: FileStatus;
  /** A folder that only exists through unsaved files: nothing to fetch for it. */
  isVirtual?: boolean;
};

/**
 * One folder's listing with the unsaved work drawn over it: deleted files
 * stay, struck through; moved-away files go; new and moved-in files appear,
 * with the folders that lead to them.
 */
export const overlayFolder = (
  ws: SourceWorkspace,
  dir: string,
  entries: SourceFolderEntry[]
): OverlaidEntry[] => {
  const prefix = dir ? `${dir}/` : "";
  const folders = new Map<string, OverlaidEntry>();
  const files = new Map<string, OverlaidEntry>();

  for (const entry of entries) {
    if (entry.type === "FOLDER") {
      folders.set(entry.name, { ...entry, status: null });
      continue;
    }
    const removed = ws.removed[entry.path];
    if (removed && removed !== "deleted") continue;
    files.set(entry.name, { ...entry, status: statusOf(ws, entry.path) });
  }

  for (const [path, file] of Object.entries(ws.present)) {
    if (!path.startsWith(prefix)) continue;
    const rest = path.slice(prefix.length);
    const slash = rest.indexOf("/");
    if (slash === -1) {
      if (file.from === path && file.content === null) continue;
      const listed = files.get(rest);
      // The size of a moved file is not known here; the API's listing of its
      // new folder says it after the save.
      const size =
        file.content !== null
          ? byteLength(file.content)
          : listed && listed.type === "FILE"
          ? listed.size
          : 0;
      files.set(rest, {
        type: "FILE",
        name: rest,
        path,
        size,
        status: statusOf(ws, path),
      });
    } else {
      const name = rest.slice(0, slash);
      if (!folders.has(name)) {
        folders.set(name, {
          type: "FOLDER",
          name,
          path: `${prefix}${name}`,
          fileCount: 0,
          status: null,
          isVirtual: true,
        });
      }
    }
  }

  const byName = (a: { name: string }, b: { name: string }) =>
    a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
  return [
    ...Array.from(folders.values()).sort(byName),
    ...Array.from(files.values()).sort(byName),
  ];
};

/** Whether unsaved files sit under `dir`: a folder that may exist only through them. */
export const hasUnsavedUnder = (ws: SourceWorkspace, dir: string) =>
  Object.keys(ws.present).some((path) => path.startsWith(`${dir}/`));

export interface Conflict {
  path: string;
  /** The text in the code now; null when the file is gone from it. */
  theirs: string | null;
  /** The text as edited here; null when the edit was to delete it. */
  mine: string | null;
}

/**
 * The unsaved work carried onto newer code. `read` answers a path's text in
 * that code, or null when it has no such file. Work on a file the newer code
 * left as it was is kept as is; work on a file it changed too comes back as
 * a conflict to settle (keepMine / takeTheirs) before saving.
 */
export const rebase = async (
  ws: SourceWorkspace,
  read: (path: string) => Promise<string | null>
): Promise<{ workspace: SourceWorkspace; conflicts: Conflict[] }> => {
  const conflicts: Conflict[] = [];
  const present: Record<string, PresentFile> = {};
  const removed: SourceWorkspace["removed"] = {};

  for (const [path, file] of Object.entries(ws.present)) {
    if (file.from === null) {
      // A new file: a conflict only if the newer code has one at this path.
      const theirs = await read(path);
      if (theirs === null) {
        present[path] = file;
      } else if (theirs === file.content) {
        // Nothing left to do: the code has this file already.
      } else {
        conflicts.push({ path, theirs, mine: file.content });
        present[path] = { ...file, original: theirs };
      }
      continue;
    }
    const theirs = await read(file.from);
    if (theirs === null) {
      // The file it came from is gone. Text typed here is still worth
      // keeping as a new file; a plain move has nothing left to move.
      if (file.content !== null) {
        conflicts.push({ path, theirs: null, mine: file.content });
        present[path] = { from: null, content: file.content, original: null };
      }
      continue;
    }
    if (file.original === null || theirs === file.original) {
      present[path] = { ...file, original: theirs };
    } else if (file.content === null || theirs === file.content) {
      // Only moved, or typed exactly what the code has now: follows the code.
      present[path] = { ...file, content: null, original: theirs };
      if (file.from === path) delete present[path];
    } else {
      conflicts.push({ path, theirs, mine: file.content });
      present[path] = { ...file, original: theirs };
    }
    if (file.from !== path && present[path]) {
      removed[file.from] = { movedTo: path };
    }
  }

  for (const [path, mark] of Object.entries(ws.removed)) {
    if (mark !== "deleted") continue;
    // Still there: the delete stands. Gone already: nothing to delete.
    if ((await read(path)) !== null) {
      removed[path] = "deleted";
    }
  }

  return { workspace: { present, removed }, conflicts };
};

/** Settles a conflict the editor's way: the text typed here stays. */
export const keepMine = (
  ws: SourceWorkspace,
  conflict: Conflict
): SourceWorkspace => {
  if (conflict.mine === null) return ws;
  const file = ws.present[conflict.path];
  return {
    ...ws,
    present: {
      ...ws.present,
      [conflict.path]: {
        from: file
          ? file.from
          : conflict.theirs === null
          ? null
          : conflict.path,
        content: conflict.mine,
        original: conflict.theirs,
      },
    },
  };
};

/** Settles a conflict the code's way: the edit here is dropped. */
export const takeTheirs = (
  ws: SourceWorkspace,
  conflict: Conflict
): SourceWorkspace => discardFile(ws, conflict.path);
